import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Stack, Typography, Paper, Box, Chip, Button, IconButton, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Select,
  MenuItem, Menu, Tooltip, Link as MuiLink, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, InputAdornment,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MergeTypeOutlinedIcon from "@mui/icons-material/MergeTypeOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVertOutlined";
import { apiGet, apiPatch, apiPost, apiFetch, ApiError } from "../api/client";
import { cacheGet, cacheSet, cacheInvalidate } from "../api/cache";
import ImageGalleryCell, { parseImageUrls } from "../components/ImageGalleryCell";
import SaleStatusChip from "../components/SaleStatusChip";
import EditableTextCell from "../components/EditableTextCell";
import { useToast } from "../ui/Toaster";

// Mirrors the dropdown shapes Inventory.js exposes — keeping the union here
// (rather than importing from Inventory.js) avoids a circular-style coupling
// where editing the inventory list page would silently change the detail
// page's allowed values.
const CONDITION_OPTIONS = ["", "New", "Open Box", "Used", "For Parts"];
const SHELVES = Array.from({ length: 5 }, (_, j) => j + 1);
const POS_LIMIT = { a: 8, b: 5, c: 5, d: 7, e: 7, f: 7, g: 7 };
const ROW_LETTERS = ["a", "b", "c", "d", "e", "f", "g"];
const ROW_LOCATIONS = ROW_LETTERS.flatMap((row) => {
  const max = POS_LIMIT[row] || 0;
  const positions = Array.from({ length: max }, (_, i) => i + 1);
  return positions.flatMap((pos) => SHELVES.map((shelf) => `${row}${pos}-${shelf}`));
});
// Includes the three terminal sentinels added later (Personal,
// Abandoned, Refunded) so this page's Select can display them when an
// item is already in one of those states. "Returned (Assurance)" stays
// OUT — see Inventory.js for the reasoning: it's set exclusively via
// the kebab action so the cost-zeroing side-effect runs.
const LOCATION_OPTIONS = ["", "Home", "Warehouse", "Sold", "Trash", "Missing", "Personal", "Abandoned", "Refunded", "Other", ...ROW_LOCATIONS];

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const formatDate = (unix) => {
  if (!unix) return "—";
  return new Date(Number(unix) * 1000).toLocaleDateString();
};
// Relative-time string for location-history rows. Cheap; no date-fns
// dependency since the inventory page doesn't already have it.
const formatRelative = (unix) => {
  if (!unix) return "";
  const seconds = Math.max(0, Math.floor(Date.now() / 1000) - Number(unix));
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 86400 * 30) return `${Math.floor(seconds / 86400)}d ago`;
  if (seconds < 86400 * 365) return `${Math.floor(seconds / (86400 * 30))}mo ago`;
  return `${Math.floor(seconds / (86400 * 365))}y ago`;
};
const formatDateTime = (unix) => {
  if (!unix) return "—";
  return new Date(Number(unix) * 1000).toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
};

// Status precedence (highest priority first):
//   Sold > Refunded/Personal/Abandoned/Returned-Assurance (location-based
//   terminal states) > Listed > Idle.
// The location-based terminals beat "Listed" because once a lot is
// marked terminal we've auto-delisted server-side; the local listed=1
// flag may not have caught up yet.
const _TERMINAL_LOCATION_CHIPS = {
  refunded:               { label: "Refunded",  color: "default" },
  personal:               { label: "Personal",  color: "warning" },
  abandoned:              { label: "Abandoned", color: "error"   },
  "returned (assurance)": { label: "Returned",  color: "default" },
  trash:                  { label: "Trash",     color: "error"   },
  missing:                { label: "Missing",   color: "warning" },
};
function StatusChip({ row }) {
  if (row.sold) return <Chip size="small" label="Sold" color="success" variant="outlined" />;
  const loc = (row.location || "").trim().toLowerCase();
  const term = _TERMINAL_LOCATION_CHIPS[loc];
  if (term) return <Chip size="small" label={term.label} color={term.color} variant="outlined" />;
  if (row.listed) return <Chip size="small" label="Listed" color="primary" variant="outlined" />;
  return <Chip size="small" label="Idle" variant="outlined" />;
}

function StatTile({ label, value, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.75, flex: 1, minWidth: 140 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, fontSize: 11 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums", color: color || "text.primary" }}>
        {value}
      </Typography>
    </Paper>
  );
}

// Discount dialog — same wire format as EbayListings.js's DiscountModal but
// inlined here so the detail page doesn't pull in EbayListings's other
// (unrelated) dialogs. POSTs to /api/discount/<item_id> per spec.
function DiscountDialog({ open, onClose, listing, onApplied }) {
  const { error: toastError, success: toastSuccess } = useToast();
  const [percent, setPercent] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setPercent(""); }, [open]);

  const p = parseInt(percent || "0", 10);
  const newPrice = listing && isFinite(p) && p > 0 && p < 100
    ? Math.max(0, Math.round(listing.price * (1 - p / 100)) - 0.01).toFixed(2)
    : null;

  const apply = async () => {
    if (!listing) return;
    if (!isFinite(p) || p <= 0 || p >= 100) {
      toastError("Enter a percentage between 1 and 99.");
      return;
    }
    setBusy(true);
    try {
      const res = await apiPost(`/api/discount/${encodeURIComponent(listing.item_id)}`, {
        sku: listing.lot_id,
        percent: p,
        price: listing.price,
      });
      if (res?.success) {
        toastSuccess("Discount applied", `${listing.lot_id} → $${(res.new_price ?? newPrice)}`);
        onApplied?.();
        onClose?.();
      } else {
        toastError("Discount rejected", res?.error || res?.message || "");
      }
    } catch (e) {
      toastError("Failed to apply", e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Apply discount</DialogTitle>
      <DialogContent>
        {listing && (
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            {listing.title || listing.lot_id} • current price {money(listing.price)}
          </Typography>
        )}
        <Stack direction="row" alignItems="flex-end" gap={2}>
          <TextField
            type="number"
            inputProps={{ min: 1, max: 99 }}
            placeholder="% off"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            size="small"
            autoFocus
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocalOfferOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {newPrice && (
            <Box sx={{ textAlign: "right", minWidth: 90 }}>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                New price
              </Typography>
              <Typography variant="h6" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                ${newPrice}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          onClick={apply}
          variant="contained"
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function InventoryDetail() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { error: toastError, success: toastSuccess } = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [discountTarget, setDiscountTarget] = useState(null);
  // SKUs with an in-flight inline edit — disables the cell + dims it while saving.
  const [savingSkus, setSavingSkus] = useState(() => new Set());
  // Lots whose List button has been clicked. The /api/list_item endpoint
  // is fire-and-forget (returns 202 immediately) but list_single_item
  // takes ~5-15s on eBay's side, so we keep the spinner up briefly and
  // re-fetch the inventory detail after a short delay so the row's
  // status chip flips from "Idle" to "Listed" without a manual reload.
  const [listingLots, setListingLots] = useState(() => new Set());

  // Per-lot action menu state. The kebab in the Actions column opens
  // a dropdown letting the operator mark this lot Refunded / Returned
  // (Assurance) / Personal. Server-side endpoints handle the cost
  // mutation and auto-delist.
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuLot, setActionMenuLot] = useState(null);
  const [actionMenuCost, setActionMenuCost] = useState(0);
  const closeActionMenu = () => { setActionMenuAnchor(null); setActionMenuLot(null); };

  // Package shipping dimensions (weight + L/W/H). Stored on each item
  // row server-side; surfaced as one product-level set on this page. The
  // save endpoint pushes to every active listing in the group so eBay's
  // shipping calculator picks up the new size right away.
  const [pkgDraft, setPkgDraft] = useState(null);  // {weight, length, width, height} | null
  const [pkgSaving, setPkgSaving] = useState(false);
  // Title-rename dialog state. ``null`` = closed; otherwise object holding
  // the in-progress draft + busy flag for the Save button.
  const [titleDraft, setTitleDraft] = useState(null);
  const [titleBusy, setTitleBusy] = useState(false);

  // Single fetch — the inventory detail endpoint is now pure local-DB and
  // returns lots/sales/returns/aggregates/listings in one payload. Cached so
  // revisits paint instantly while a background refresh confirms the data.
  const cacheKeyDetail = `/api/inventory/${key}`;

  const load = useCallback(
    async (signal) => {
      setNotFound(false);
      const cached = cacheGet(cacheKeyDetail);
      if (cached) {
        setData(cached.data);
        setLoading(false);
      } else {
        setLoading(true);
      }
      try {
        const res = await apiGet(`/api/inventory/${encodeURIComponent(key)}`, { signal });
        if (signal?.aborted) return;
        setData(res);
        cacheSet(cacheKeyDetail, res);
        // Seed the package-dims draft from the server-side product values.
        // Strings (not numbers) so empty inputs serialize cleanly back to
        // null on save.
        const pd = res?.package_dims || {};
        setPkgDraft({
          weight: pd.weight_lbs != null ? String(pd.weight_lbs) : "",
          length: pd.length_in  != null ? String(pd.length_in)  : "",
          width:  pd.width_in   != null ? String(pd.width_in)   : "",
          height: pd.height_in  != null ? String(pd.height_in)  : "",
        });
      } catch (e) {
        if (e?.name === "AbortError") return;
        if (e instanceof ApiError && e.status === 404) {
          setNotFound(true);
          setData(null);
        } else if (!cached) {
          toastError("Couldn't load product", e instanceof ApiError ? e.message : String(e));
          setData(null);
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, toastError]
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  // Hash-anchor scroll: when the user lands on this page via a link like
  // ``/admint/inventory/foo#listings`` (from the eBay Listings page row
  // actions), scroll the matching <Paper id="…"> into view once the
  // data has loaded and the sections have rendered.
  useEffect(() => {
    if (!data) return;
    const hash = window.location.hash?.replace(/^#/, "");
    if (!hash) return;
    // Two animation frames so the section's been laid out — one frame is
    // sometimes too early when MUI's tables are still measuring.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }));
  }, [data]);

  // Optimistic per-lot location/condition update with rollback on error.
  // Mirrors Inventory.js's updateField pattern exactly so the same backend
  // (PATCH /api/update_location, /api/update_condition) handles both pages.
  // Save package weight + L/W/H. Pushes to every active listing in the
  // group via the backend's eBay-side update, so the shipping calculator
  // refreshes immediately (operator doesn't have to wait for a re-publish).
  const savePackageDims = useCallback(async () => {
    if (!pkgDraft) return;
    const toNum = (s) => {
      const t = (s ?? "").toString().trim();
      if (!t) return null;
      const n = Number(t);
      return Number.isFinite(n) && n >= 0 ? n : "_invalid";
    };
    const body = {
      weight_lbs: toNum(pkgDraft.weight),
      length_in:  toNum(pkgDraft.length),
      width_in:   toNum(pkgDraft.width),
      height_in:  toNum(pkgDraft.height),
      push_to_ebay: true,
    };
    if (Object.values(body).includes("_invalid")) {
      toastError("Bad dimensions", "All values must be positive numbers (or blank for default)");
      return;
    }
    setPkgSaving(true);
    try {
      const res = await apiPost(`/api/inventory/${encodeURIComponent(key)}/package_dims`, body);
      if (!res?.success) {
        toastError("Couldn't save dimensions", res?.error || "Unknown error");
        return;
      }
      const pushed = (res.pushed_to_ebay || []).length;
      const failed = (res.failed_ebay || []).length;
      toastSuccess(
        "Package saved",
        pushed > 0
          ? `${res.applied_to?.length || 0} lot(s) · ${pushed} eBay listing(s) updated${failed ? ` · ${failed} eBay push failed` : ""}`
          : `${res.applied_to?.length || 0} lot(s) — will apply on next publish`,
      );
      cacheInvalidate(cacheKeyDetail);
      cacheInvalidate("/api/inventory");
      load();
    } catch (e) {
      toastError("Couldn't save dimensions", e instanceof ApiError ? e.message : String(e));
    } finally {
      setPkgSaving(false);
    }
  }, [pkgDraft, key, cacheKeyDetail, load, toastSuccess, toastError]);

  // Manual "List on eBay" trigger per lot. The backend endpoint is
  // fire-and-forget (returns 202 + queues list_single_item in a thread),
  // so we just toast on accept + show a spinner on the row for a few
  // seconds while eBay does its thing. After the timeout we re-fetch
  // /api/inventory/<key> so the row's status chip flips from "Idle"
  // to "Listed" naturally.
  const listLot = useCallback(async (lotId) => {
    if (!lotId || listingLots.has(lotId)) return;
    setListingLots((cur) => {
      const n = new Set(cur);
      n.add(lotId);
      return n;
    });
    try {
      const res = await apiPost(`/api/list_item/${encodeURIComponent(lotId)}`);
      if (res?.success || res?.queued) {
        toastSuccess("Listing kicked off", `${lotId} — eBay publish in progress (~10s)`);
      } else {
        toastError("Couldn't list", res?.error || "Unknown error");
      }
    } catch (e) {
      toastError("Couldn't list", e instanceof ApiError ? e.message : String(e));
    }
    // Re-fetch after a delay so the status chip reflects the new
    // listings row. 12s is enough headroom for the typical
    // list_single_item run on a clean cache.
    setTimeout(() => {
      cacheInvalidate("/api/inventory");
      cacheInvalidate(cacheKeyDetail);
      load();
      setListingLots((cur) => {
        const n = new Set(cur);
        n.delete(lotId);
        return n;
      });
    }, 12000);
  }, [listingLots, toastSuccess, toastError, cacheKeyDetail, load]);

  const updateLotField = useCallback(
    async (lotId, field, value) => {
      const prev = data;
      setData((cur) =>
        cur
          ? {
              ...cur,
              lots: cur.lots.map((r) => (r.lot_id === lotId ? { ...r, [field]: value } : r)),
            }
          : cur
      );
      try {
        const path = field === "location" ? "/api/update_location" : "/api/update_condition";
        await apiPatch(path, { lot_id: lotId, [field]: value });
        // Lot field changed — drop both the consolidated inventory cache and
        // this product's detail cache so they don't display stale values.
        cacheInvalidate("/api/inventory");
        toastSuccess(`Updated ${field}`, `${lotId} → ${value || "(empty)"}`);
      } catch (e) {
        setData(prev); // rollback
        toastError(`Failed to update ${field}`, e instanceof ApiError ? e.message : String(e));
      }
    },
    [data, toastSuccess, toastError]
  );

  // Shared mark-action helper for the per-lot kebab. POSTs to one of
  // the dedicated endpoints, updates the row optimistically with the
  // new location, and rolls back on failure. Auto-delist of any active
  // listing is handled server-side via _mark_item_with_delist().
  const _runLotMark = useCallback(
    async ({ lotId, endpoint, body, location, confirmMsg, toast }) => {
      if (confirmMsg && !window.confirm(confirmMsg)) return;
      const prev = data;
      setData((cur) =>
        cur
          ? {
              ...cur,
              lots: cur.lots.map((r) =>
                r.lot_id === lotId ? { ...r, location } : r
              ),
            }
          : cur
      );
      try {
        const r = await apiPost(
          `/api/items/${encodeURIComponent(lotId)}${endpoint}`,
          body || undefined,
        );
        if (r?.success) {
          cacheInvalidate("/api/inventory");
          toastSuccess(toast.title, toast.msg);
        } else {
          setData(prev);
          toastError("Mark failed", r?.error || "Unknown error");
        }
      } catch (e) {
        setData(prev);
        toastError("Mark failed", e instanceof ApiError ? e.message : String(e));
      }
    },
    [data, toastSuccess, toastError]
  );

  const markRefunded = useCallback((lotId) =>
    _runLotMark({
      lotId, endpoint: "/mark_refunded", location: "Refunded",
      confirmMsg: `Mark ${lotId} as Refunded?\n\nCost zeroed (you got your money back). `
        + "Item stays in the DB for the audit trail. Auto-delists from eBay/FB if listed.",
      toast: { title: "Marked Refunded", msg: lotId },
    }),
    [_runLotMark]);

  const markReturnedAssurance = useCallback((lotId, originalCost) => {
    const fee = Number(originalCost || 0) >= 100 ? 10 : 5;
    return _runLotMark({
      lotId, endpoint: "/mark_returned_assurance",
      body: { assurance_fee: fee },
      location: "Returned (Assurance)",
      confirmMsg: `Mark ${lotId} as Returned via Buyer's Assurance?\n\n`
        + `Effective cost becomes $${fee.toFixed(2)} (assurance fee). `
        + `Auto-delists from eBay/FB if listed.`,
      toast: { title: "Marked Returned (Assurance)", msg: `${lotId} → $${fee.toFixed(2)} loss` },
    });
  }, [_runLotMark]);

  const markPersonal = useCallback((lotId) =>
    _runLotMark({
      lotId, endpoint: "/mark_personal", location: "Personal",
      confirmMsg: `Mark ${lotId} as Personal?\n\nFull cost shows as a personal write-off. `
        + "Auto-delists from eBay/FB if listed.",
      toast: { title: "Marked Personal", msg: lotId },
    }),
    [_runLotMark]);

  // Rename the consolidated product. Backend updates every lot's title and,
  // if the new slug already has an active keeper, ends this group's listings
  // and bumps the keeper's qty (auto-merge — same idea as OrderChecker).
  const saveTitle = useCallback(async (newTitle) => {
    const t = (newTitle || "").trim();
    if (!t || t === (data?.cleaned_title || data?.title || "")) {
      setTitleDraft(null);
      return;
    }
    setTitleBusy(true);
    try {
      const res = await apiPatch(`/api/inventory/${encodeURIComponent(key)}`, { title: t });
      if (!res?.success) {
        toastError("Couldn't rename", res?.error || "Update failed");
        return;
      }
      const merges = Array.isArray(res.merges) ? res.merges : [];
      if (merges.length > 0) {
        const total = merges.reduce((acc, m) => acc + (Number(m.qty_after) - Number(m.qty_before)), 0);
        toastSuccess(
          "Renamed & merged",
          `${res.applied_to?.length || 0} lot(s) renamed; ${merges.length} listing(s) merged into existing keeper${total ? ` (+${total} qty)` : ""}`,
        );
      } else {
        toastSuccess(
          "Renamed",
          `${res.applied_to?.length || 0} lot(s) updated`,
        );
      }
      // Drop caches so consolidated inventory + this product reflect the new
      // title/grouping immediately.
      cacheInvalidate("/api/inventory");
      cacheInvalidate("/api/listings");
      cacheInvalidate(cacheKeyDetail);
      setTitleDraft(null);
      // The slug changed → URL is now stale. Navigate to the new key so the
      // page rehydrates against the new product. (No-op if slug stayed the
      // same, which the backend's noop_slug flag tells us.)
      if (res.new_key && res.new_key !== key) {
        navigate(`/admint/inventory/${encodeURIComponent(res.new_key)}`);
      } else {
        // Same slug, just cosmetic title change → reload current page data.
        load();
      }
    } catch (e) {
      toastError("Couldn't rename", e instanceof ApiError ? e.message : String(e));
    } finally {
      setTitleBusy(false);
    }
  }, [data, key, navigate, toastError, toastSuccess, load, cacheKeyDetail]);

  // Inline-edit a listing field (price/quantity/condition/title) via the
  // shared PATCH /api/listings/<sku> endpoint. Optimistic update, reconciles
  // to whatever the backend actually applied, rolls back on failure.
  const savePatchListing = useCallback(async (listingRow, patch) => {
    const sku = listingRow.lot_id;  // listings.sku == lot_id by convention
    if (!sku) return;
    setSavingSkus((prev) => {
      const n = new Set(prev);
      n.add(sku);
      return n;
    });
    setData((cur) => {
      if (!cur) return cur;
      return {
        ...cur,
        listings: cur.listings.map((l) =>
          l.lot_id === sku ? { ...l, ...patch } : l
        ),
      };
    });
    try {
      const res = await apiFetch(`/api/listings/${encodeURIComponent(sku)}`, {
        method: "PATCH",
        json: patch,
      });
      if (res?.success) {
        const applied = res.applied || {};
        setData((cur) => {
          if (!cur) return cur;
          return {
            ...cur,
            listings: cur.listings.map((l) =>
              l.lot_id === sku ? { ...l, ...applied } : l
            ),
          };
        });
        cacheInvalidate("/api/inventory");
        cacheInvalidate("/api/listings");
        const fields = Object.keys(applied).join(", ") || "saved";
        toastSuccess("Updated", `${sku} → ${fields}`);
      } else {
        // Server explicitly rejected — revert to original row state.
        setData((cur) => {
          if (!cur) return cur;
          return {
            ...cur,
            listings: cur.listings.map((l) =>
              l.lot_id === sku ? { ...listingRow } : l
            ),
          };
        });
        toastError("Couldn't save", res?.error || "Update failed");
      }
    } catch (e) {
      setData((cur) => {
        if (!cur) return cur;
        return {
          ...cur,
          listings: cur.listings.map((l) =>
            l.lot_id === sku ? { ...listingRow } : l
          ),
        };
      });
      toastError("Couldn't save", e instanceof ApiError ? e.message : String(e));
    } finally {
      setSavingSkus((prev) => {
        const n = new Set(prev);
        n.delete(sku);
        return n;
      });
    }
  }, [toastSuccess, toastError]);

  // Build the per-lot spend share so the sales table can show profit per
  // sale. Spend per lot = cost + tax + premium for that specific lot. When
  // a sale links multiple lots in this product the share sums those lots'
  // individual spends (other unrelated lots in a multi-product order are
  // already filtered out by the backend payload).
  const spendByLot = useMemo(() => {
    const m = new Map();
    for (const r of data?.lots || []) {
      m.set(r.lot_id, (Number(r.cost) || 0) + (Number(r.tax_cost) || 0) + (Number(r.premium_fee) || 0));
    }
    return m;
  }, [data]);

  // Carousel images for the page header — pulled from the first lot's
  // image_urls so we don't try to dedupe across visually-identical variants.
  const headerImages = useMemo(() => {
    const first = data?.image_urls;
    return parseImageUrls(first).slice(0, 6);
  }, [data]);

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (notFound || !data) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Inventory2OutlinedIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>Product not found</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          We couldn't find any inventory for "{key}".
        </Typography>
        <Button component={RouterLink} to="/admint/inventory" variant="contained">
          Back to inventory
        </Button>
      </Box>
    );
  }

  const agg = data.aggregates || {};
  const profitColor = (n) => (n > 0 ? "success.main" : n < 0 ? "error.main" : "text.secondary");
  const ebaySearchUrl = `https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(data.cleaned_title || data.title || "")}`;

  return (
    <Box>
      {/* Header — back arrow + title + brand subtitle + right-aligned actions */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ minWidth: 0 }}>
          <IconButton
            onClick={() => navigate("/admint/inventory")}
            aria-label="Back to inventory"
            sx={{ mt: 0.25 }}
          >
            <ArrowBackOutlinedIcon />
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.5 }}>
              <Typography
                variant="h1"
                sx={{ fontSize: { xs: "1.6rem", md: "2.25rem" }, lineHeight: 1.15, wordBreak: "break-word", flex: 1, minWidth: 0 }}
              >
                {data.cleaned_title || data.title || "Untitled product"}
              </Typography>
              <Tooltip title="Rename product (auto-merges if title matches another)">
                <IconButton
                  size="small"
                  aria-label="Rename product"
                  onClick={() => setTitleDraft(data.cleaned_title || data.title || "")}
                  sx={{ mt: { xs: 0.25, md: 0.75 } }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
              {data.brand && (
                <Chip size="small" label={data.brand} variant="outlined" sx={{ borderRadius: 0.75 }} />
              )}
              <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                {agg.total_qty} lot{agg.total_qty === 1 ? "" : "s"} · {agg.sold_qty} sold · {agg.active_qty} active
              </Typography>
            </Stack>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          {data.listings && data.listings.length > 0 && (
            <Button
              component={MuiLink}
              href={ebaySearchUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              startIcon={<OpenInNewOutlinedIcon />}
            >
              Open in eBay
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Image carousel — up to 6 thumbnails from the first lot's image_urls */}
      {headerImages.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2.5, flexWrap: "wrap", rowGap: 1 }}>
          {headerImages.map((src, i) => (
            <Box
              key={`${src}-${i}`}
              component="img"
              src={src}
              alt={`thumbnail ${i + 1}`}
              loading="lazy"
              sx={{
                width: 84, height: 84, objectFit: "cover",
                border: 1, borderColor: "divider", borderRadius: 1,
              }}
            />
          ))}
        </Stack>
      )}

      {/* Top KPI tiles */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 3 }}>
        <StatTile label="Active qty" value={agg.active_qty ?? 0} />
        <StatTile label="Sold qty" value={agg.sold_qty ?? 0} />
        <StatTile label="Total spend" value={money(agg.total_spend)} />
        <StatTile
          label="Net profit"
          value={money(agg.total_profit)}
          color={profitColor(Number(agg.total_profit) || 0)}
        />
      </Stack>

      {/* Shipping package: one set per product. Empty fields fall back to
          eBay's default 2 lb · 12×16×8 in box when the listing is built.
          Save also pushes to every active listing in the group so the
          eBay shipping calculator updates right away. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "flex-end" }}>
          <Box sx={{ minWidth: 180 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Package</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Weight + box dimensions for eBay shipping. Leave blank for default.
            </Typography>
          </Box>
          <TextField
            label="Weight (lbs)"
            size="small"
            type="number"
            value={pkgDraft?.weight ?? ""}
            onChange={(e) => setPkgDraft((d) => ({ ...(d || {}), weight: e.target.value }))}
            inputProps={{ min: 0, step: 0.1 }}
            sx={{ width: 140 }}
          />
          <TextField
            label="Length (in)"
            size="small"
            type="number"
            value={pkgDraft?.length ?? ""}
            onChange={(e) => setPkgDraft((d) => ({ ...(d || {}), length: e.target.value }))}
            inputProps={{ min: 0, step: 0.5 }}
            sx={{ width: 120 }}
          />
          <TextField
            label="Width (in)"
            size="small"
            type="number"
            value={pkgDraft?.width ?? ""}
            onChange={(e) => setPkgDraft((d) => ({ ...(d || {}), width: e.target.value }))}
            inputProps={{ min: 0, step: 0.5 }}
            sx={{ width: 120 }}
          />
          <TextField
            label="Height (in)"
            size="small"
            type="number"
            value={pkgDraft?.height ?? ""}
            onChange={(e) => setPkgDraft((d) => ({ ...(d || {}), height: e.target.value }))}
            inputProps={{ min: 0, step: 0.5 }}
            sx={{ width: 120 }}
          />
          <Button
            variant="contained"
            onClick={savePackageDims}
            disabled={pkgSaving}
            startIcon={pkgSaving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ whiteSpace: "nowrap" }}
          >
            {pkgSaving ? "Saving…" : "Save package"}
          </Button>
        </Stack>
      </Paper>

      {/* Lots table */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Lots</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {data.lots.length} per-lot row{data.lots.length === 1 ? "" : "s"}
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small" sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 130 }}>Lot</TableCell>
                <TableCell sx={{ width: 160 }}>Location</TableCell>
                <TableCell sx={{ width: 150 }}>Condition</TableCell>
                <TableCell sx={{ width: 110 }}>Status</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>Value</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>Spend</TableCell>
                <TableCell sx={{ width: 120 }}>Acquired</TableCell>
                {/* Actions: List / etc. — collapses to nothing on rows that
                    don't need any (already sold or already listed). */}
                <TableCell sx={{ width: 110 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.lots.map((row) => {
                const spend = (Number(row.cost) || 0) + (Number(row.tax_cost) || 0) + (Number(row.premium_fee) || 0);
                const listing = listingLots.has(row.lot_id);
                // A lot is "listable" when it isn't already sold and isn't
                // already on eBay (items.listed=0). Auto-relist also fires
                // when location flips Sold → shippable, but this button
                // covers every other case (forgot to set value first,
                // listing was manually delisted, etc.). Terminal-location
                // lots (refunded / personal / abandoned / returned /
                // trash / missing) are no longer in our hands, so the
                // List button is hidden for them too.
                const _locLower = (row.location || "").trim().toLowerCase();
                const canList = !row.sold && !row.listed
                  && !_TERMINAL_LOCATION_CHIPS[_locLower];
                return (
                  <TableRow key={row.lot_id} hover>
                    <TableCell sx={{ fontFamily: "mono", fontSize: "0.78rem" }}>{row.lot_id}</TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={row.location || ""}
                        onChange={(e) => updateLotField(row.lot_id, "location", e.target.value)}
                        displayEmpty
                        sx={{ minWidth: 140 }}
                      >
                        {LOCATION_OPTIONS.map((o) => (
                          <MenuItem key={o} value={o}>{o || "—"}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={row.condition || ""}
                        onChange={(e) => updateLotField(row.lot_id, "condition", e.target.value)}
                        displayEmpty
                        sx={{ minWidth: 130 }}
                      >
                        {CONDITION_OPTIONS.map((o) => (
                          <MenuItem key={o} value={o}>{o || "—"}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell><StatusChip row={row} /></TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>
                      {money(row.value)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>
                      {money(spend)}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{formatDate(row.purchase_date)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                        {canList && (
                          <Tooltip title="Publish this lot to eBay">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={listing
                                ? <CircularProgress size={12} color="inherit" />
                                : <SellOutlinedIcon fontSize="small" />}
                              disabled={listing}
                              onClick={() => listLot(row.lot_id)}
                            >
                              {listing ? "Listing…" : "List"}
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip title="Lot actions — mark refunded / returned / personal">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setActionMenuAnchor(e.currentTarget);
                              setActionMenuLot(row.lot_id);
                              setActionMenuCost(
                                (Number(row.cost) || 0)
                                + (Number(row.tax_cost) || 0)
                                + (Number(row.premium_fee) || 0)
                              );
                            }}
                            sx={{ p: 0.5 }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Active eBay Listings */}
      <Paper id="listings" variant="outlined" sx={{ mb: 3, scrollMarginTop: 80 }}>
        <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Active eBay listings</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {data.listings.length} linked
          </Typography>
        </Box>
        {data.listings.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
            <Typography variant="body2">No linked listings yet — match them on the Match page.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 80 }}>Image</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell sx={{ width: 130 }}>Lot</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>Price</TableCell>
                  <TableCell align="right" sx={{ width: 80 }}>Qty</TableCell>
                  <TableCell align="right" sx={{ width: 110 }}>Days unsold</TableCell>
                  <TableCell sx={{ width: 200 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.listings.map((row) => {
                  const busy = savingSkus.has(row.lot_id);
                  return (
                    <TableRow key={row.item_id || row.lot_id} hover>
                      <TableCell sx={{ width: 80 }}>
                        <ImageGalleryCell imageUrlsStr={row.image_urls} thumbnailSize={48} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", maxWidth: 360 }}>
                          {row.title || row.cleaned_title || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: "mono", fontSize: "0.78rem" }}>{row.lot_id}</TableCell>
                      <TableCell align="right">
                        <EditableTextCell
                          value={row.price ?? ""}
                          type="number"
                          align="right"
                          busy={busy}
                          inputProps={{ step: "0.01", min: "0.01" }}
                          onCommit={(v) => {
                            const n = Number(v);
                            if (!Number.isFinite(n) || n <= 0) return;
                            savePatchListing(row, { price: Number(n.toFixed(2)) });
                          }}
                          displayValue={
                            <Box sx={{ fontVariantNumeric: "tabular-nums" }}>{money(row.price)}</Box>
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <EditableTextCell
                          value={row.quantity ?? ""}
                          type="number"
                          align="right"
                          busy={busy}
                          inputProps={{ min: "0", step: "1" }}
                          onCommit={(v) => {
                            const n = parseInt(v, 10);
                            if (!Number.isFinite(n) || n < 0) return;
                            savePatchListing(row, { quantity: n });
                          }}
                          displayValue={
                            <Box sx={{ fontVariantNumeric: "tabular-nums" }}>
                              {row.quantity ?? "—"}
                            </Box>
                          }
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>
                        {row.days_unsold ?? "—"}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<LocalOfferOutlinedIcon fontSize="small" />}
                            onClick={() => setDiscountTarget(row)}
                          >
                            Discount
                          </Button>
                          {row.item_id && (
                            <Tooltip title="Open eBay listing">
                              <IconButton
                                size="small"
                                component={MuiLink}
                                href={`https://www.ebay.com/itm/${row.item_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <OpenInNewOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Sales history */}
      <Paper id="sales" variant="outlined" sx={{ mb: 3, scrollMarginTop: 80 }}>
        <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Sales history</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {data.sales.length} sale{data.sales.length === 1 ? "" : "s"} ·{" "}
            {money(agg.total_revenue)} revenue
          </Typography>
        </Box>
        {data.sales.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
            <Typography variant="body2">No sales yet for this product.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 160 }}>Order ID</TableCell>
                  <TableCell sx={{ width: 110 }}>Platform</TableCell>
                  <TableCell sx={{ width: 130 }}>Status</TableCell>
                  <TableCell align="right" sx={{ width: 60 }}>Qty</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>Sold price</TableCell>
                  <TableCell sx={{ width: 110 }}>Sold</TableCell>
                  <TableCell sx={{ width: 180 }}>Tracking</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>Profit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.sales.map((sale) => {
                  // Per-sale spend share = sum of cost+tax+premium for the
                  // lots this product contributes to the sale. Profit then
                  // mirrors /api/sales' formula (sold - spend - fee - ship)
                  // restricted to this product's slice.
                  const spendShare = (sale.lot_ids || []).reduce(
                    (acc, lid) => acc + (spendByLot.get(lid) || 0),
                    0
                  );
                  const profit =
                    Number(sale.sold_price || 0) -
                    spendShare -
                    Number(sale.shipping_cost || 0) -
                    Number(sale.platform_fee || 0);
                  // Qty = number of this product's lots linked to the sale.
                  // For a multi-qty single-line order this matches the
                  // eBay-side line item quantity.
                  const qty = Array.isArray(sale.lot_ids) ? sale.lot_ids.length : 1;
                  return (
                    <TableRow key={sale.id} hover>
                      <TableCell sx={{ fontFamily: "mono", fontSize: "0.78rem" }}>
                        {sale.order_id || `#${sale.id}`}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={sale.sold_at || "—"} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <SaleStatusChip
                          status={sale.status}
                          refundAmount={sale.refund_amount}
                          soldPrice={sale.sold_price}
                        />
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          fontWeight: qty > 1 ? 700 : 400,
                          color: qty > 1 ? "warning.dark" : "text.primary",
                        }}
                      >
                        {qty}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {money(sale.sold_price)}
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>{formatDate(sale.sold_date)}</TableCell>
                      <TableCell sx={{ color: "text.secondary", fontFamily: "mono", fontSize: "0.78rem" }}>
                        {sale.tracking_number || "—"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontVariantNumeric: "tabular-nums", color: profitColor(profit), fontWeight: 600 }}
                      >
                        {money(profit)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Location history — every old→new move recorded for the lots in
          this product. Capped per-lot at 50 / overall at 500 by the
          backend so the panel stays cheap on items with churny history.
          Always rendered (even when empty) so the operator knows the
          section exists — the empty state is its own affordance. */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Location history</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {data.location_history && data.location_history.length > 0
              ? `${data.location_history.length} move${data.location_history.length === 1 ? "" : "s"} across ${data.lots.length} lot${data.lots.length === 1 ? "" : "s"}`
              : `No location changes recorded across ${data.lots.length} lot${data.lots.length === 1 ? "" : "s"}`}
          </Typography>
        </Box>
        {(!data.location_history || data.location_history.length === 0) ? (
          <Box sx={{ px: 2, py: 3, color: "text.secondary", fontSize: 13 }}>
            No moves yet. The first location set on a lot, and every subsequent change,
            will appear here.
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 170 }}>When</TableCell>
                  <TableCell sx={{ width: 120 }}>Lot</TableCell>
                  <TableCell sx={{ width: 130 }}>From</TableCell>
                  <TableCell sx={{ width: 130 }}>To</TableCell>
                  <TableCell sx={{ width: 110 }}>Source</TableCell>
                  <TableCell>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.location_history.map((row, i) => (
                  <TableRow key={`${row.lot_id}-${row.changed_at}-${i}`} hover>
                    <TableCell sx={{ color: "text.secondary" }}>
                      <Tooltip title={formatDateTime(row.changed_at)} arrow placement="top">
                        <Box component="span">{formatRelative(row.changed_at)}</Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.78rem" }}>
                      {row.lot_id}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {row.old_location ? (
                        <Chip size="small" label={row.old_location} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                      ) : (
                        <Box component="span" sx={{ color: "text.disabled" }}>(unset)</Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.new_location || "(unset)"}
                        variant="filled"
                        color={
                          row.new_location === "Sold" ? "success"
                          : row.new_location === "Trash" ? "error"
                          : row.new_location === "Missing" ? "warning"
                          : "default"
                        }
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                      {row.source || "—"}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: "0.8rem", whiteSpace: "normal" }}>
                      {row.note || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Returns — visible only when non-empty */}
      {data.returns && data.returns.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Returns</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {data.returns.length} return{data.returns.length === 1 ? "" : "s"} · {money(agg.total_refunds)} refunded
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small" sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 150 }}>Return ID</TableCell>
                  <TableCell sx={{ width: 160 }}>Order</TableCell>
                  <TableCell sx={{ width: 120 }}>Date</TableCell>
                  <TableCell sx={{ width: 110 }}>Platform</TableCell>
                  <TableCell align="right" sx={{ width: 110 }}>Refund</TableCell>
                  <TableCell align="right" sx={{ width: 110 }}>Return ship</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.returns.map((ret) => (
                  <TableRow key={ret.return_id || `${ret.sale_id}-${ret.order_id}`} hover>
                    <TableCell sx={{ fontFamily: "mono", fontSize: "0.78rem" }}>{ret.return_id || "—"}</TableCell>
                    <TableCell sx={{ fontFamily: "mono", fontSize: "0.78rem" }}>{ret.order_id || `#${ret.sale_id}`}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{formatDate(ret.return_date)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={ret.platform || "—"} variant="outlined" />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{money(ret.refund_amount)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>{money(ret.return_shipping_cost)}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{ret.reason || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <DiscountDialog
        open={!!discountTarget}
        onClose={() => setDiscountTarget(null)}
        listing={discountTarget}
        onApplied={() => load()}
      />

      <RenameTitleDialog
        open={titleDraft !== null}
        currentTitle={data?.cleaned_title || data?.title || ""}
        draft={titleDraft || ""}
        setDraft={(v) => setTitleDraft(v)}
        onClose={() => !titleBusy && setTitleDraft(null)}
        onSave={() => saveTitle(titleDraft)}
        busy={titleBusy}
      />
      {/* Per-lot kebab actions — refunded / returned / personal. Single
          menu instance reused across rows. */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={closeActionMenu}
      >
        <MenuItem
          onClick={() => { const lid = actionMenuLot; closeActionMenu(); if (lid) markRefunded(lid); }}
        >
          Mark as Refunded ($0 net, kept)
        </MenuItem>
        <MenuItem
          onClick={() => { const lid = actionMenuLot; const c = actionMenuCost; closeActionMenu(); if (lid) markReturnedAssurance(lid, c); }}
        >
          Mark as Returned (Assurance — ${(Number(actionMenuCost || 0) >= 100 ? 10 : 5).toFixed(2)} fee)
        </MenuItem>
        <MenuItem
          onClick={() => { const lid = actionMenuLot; closeActionMenu(); if (lid) markPersonal(lid); }}
        >
          Mark as Personal (kept for self)
        </MenuItem>
      </Menu>
    </Box>
  );
}

// Rename dialog. Kept tiny on purpose — the merge logic lives on the server,
// the dialog just collects the new title and shows what'll happen.
function RenameTitleDialog({ open, currentTitle, draft, setDraft, onClose, onSave, busy }) {
  const trimmed = (draft || "").trim();
  const unchanged = trimmed === (currentTitle || "").trim();
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <EditOutlinedIcon color="primary" />
          <span>Rename product</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Updates the title on every lot in this product. If the new title
          matches an existing product with an active eBay listing, this
          group's listing(s) will be ended and the matching keeper's
          quantity will be bumped automatically.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="New title"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          inputProps={{ maxLength: 200 }}
          helperText={`${trimmed.length}/200`}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !busy && !unchanged && trimmed.length > 0) {
              e.preventDefault();
              onSave();
            }
          }}
        />
        {!unchanged && trimmed.length > 0 && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, color: 'text.secondary' }}>
            <MergeTypeOutlinedIcon fontSize="small" />
            <Typography variant="caption">
              If another product already uses this exact title, listings will merge automatically.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={busy || unchanged || trimmed.length === 0}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
